import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock,
  User,
  CreditCard,
  ChevronRight,
  Shield,
  Settings,
  UserCheck,
  Wallet,
} from "lucide-react";

import useModal from "hooks/use-modal";
import { MainStore } from "../../stores/Main";
import API from "../../utils/api";

const ProfilePage = () => {
  const { openModal, modalTypes } = useModal();
  const { acceptedDisclosures, agency, profile, avatar, userType } =
    useContext(MainStore);
  const history = useHistory();
  const [greeting, setGreeting] = useState("");

  useEffect(() => {
    const hour = new Date().getHours();
    if (hour < 12) setGreeting("Good morning");
    else if (hour < 18) setGreeting("Good afternoon");
    else setGreeting("Good evening");
  }, []);

  const profileActions = [
    {
      id: "password",
      title: "Password & Security",
      description:
        "Update your login password and manage your account security settings. Keep your account safe with regular password updates.",
      icon: Lock,
      color: "from-blue-500 to-cyan-500",
      bgColor: "from-blue-500/10 to-cyan-500/10",
      action: () => openModal(modalTypes.ModalAccount),
      buttonText: "Manage Security",
    },
    {
      id: "general",
      title: "General Information",
      description: "Update your contact details and communication preferences.",
      icon: UserCheck,
      color: "from-purple-500 to-pink-500",
      bgColor: "from-purple-500/10 to-pink-500/10",
      action: () => history.push("/dashboard/profile/general"),
      buttonText: "Edit Information",
    },
  ];

  const conditionalActions = [
    {
      id: "payment",
      title: "Payment Methods",
      description: "Manage your payment methods.",
      icon: CreditCard,
      color: "from-green-500 to-emerald-500",
      bgColor: "from-green-500/10 to-emerald-500/10",
      action: () => history.push("/dashboard/payments/methods"),
      buttonText: "Manage Payment Methods",
      show: acceptedDisclosures && agency,
    },
  ];

  const containerAnimation = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemAnimation = {
    hidden: { y: 20, opacity: 0 },
    show: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15,
      },
    },
  };

  const ProfileCard = ({ action }) => {
    const Icon = action.icon;

    return (
      <motion.div
        variants={itemAnimation}
        whileHover={{ y: -4, transition: { duration: 0.2 } }}
        className="group relative h-full"
      >
        <div
          className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-3xl blur-xl"
          style={{
            background: `linear-gradient(135deg, ${action.color.split(" ")[1]} 0%, ${action.color.split(" ")[3]} 100%)`,
          }}
        />

        <div className="relative bg-white/[0.03] backdrop-blur-md border border-white/10 rounded-3xl p-6 hover:bg-white/[0.05] transition-all duration-300 h-full flex flex-col">
          <div className="flex items-start gap-4 mb-4">
            <motion.div
              className={`p-3 rounded-2xl bg-gradient-to-br ${action.bgColor}`}
              whileHover={{ scale: 1.05, rotate: 5 }}
              transition={{ type: "spring", stiffness: 400 }}
            >
              <Icon className={`w-6 h-6 text-white`} />
            </motion.div>

            <div className="flex-1">
              <h3 className="text-white font-semibold text-lg mb-1">
                {action.title}
              </h3>
              <p className="text-white/60 text-sm leading-relaxed">
                {action.description}
              </p>
            </div>
          </div>

          <motion.button
            onClick={action.action}
            className={`w-full mt-auto px-4 py-3 rounded-2xl bg-gradient-to-r ${action.color} text-white font-medium 
                      flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300
                      group-hover:scale-[1.02] transform`}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <span>{action.buttonText}</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </div>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto"
      >
        {/* Page Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-semibold text-white mb-2">
            Profile Settings
          </h2>
          <p className="text-white/60">
            Manage your account settings and preferences
          </p>
        </motion.div>

        {/* Action Cards Grid */}
        <motion.div
          variants={containerAnimation}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
        >
          {/* For consumer-quick-access users, only show payment methods */}
          {userType === "consumer-quick-access" ? (
            conditionalActions
              .filter((action) => action.id === "payment" && action.show)
              .map((action) => (
                <ProfileCard key={action.id} action={action} />
              ))
          ) : (
            <>
              {/* Primary Actions */}
              {profileActions.map((action) => (
                <ProfileCard key={action.id} action={action} />
              ))}

              {/* Conditional Actions */}
              {conditionalActions
                .filter((action) => action.show)
                .map((action) => (
                  <ProfileCard key={action.id} action={action} />
                ))}
            </>
          )}
        </motion.div>

        {/* Background Gradient Orbs */}
        <motion.div
          className="fixed top-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"
          animate={{
            x: [0, 30, 0],
            y: [0, -30, 0],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      </motion.div>
    </div>
  );
};

export default ProfilePage;
